

import {Vue, Component, Watch} from 'vue-property-decorator';
import {BIconDownload, BDropdown} from "bootstrap-vue";
import {Ax} from '@/utils';
import axios from "axios";
import DatePicker from 'vue2-datepicker';
import AlterationI18nMixin from "@/modules/budget/alteration/mixins/alteration-i18n-mixin";
import {getActualVariant} from "@/modules/budget/budgetVariantService";
import BudgetPreviewCost from "@/modules/budget/components/BudgetPreviewCost.vue";
import BudgetPreviewIncome from "@/modules/budget/components/BudgetPreviewIncome.vue";
import {formatDigitsGrouping, rounding} from "@/modules/budget/monitoring/reports-constructor/common/utils/formattingUtils";


interface FormStatusRow {
  date: string;
  budget_fact520_data: string;
  budget_fact534_1: string;
  budget_fact534_G: string;
  budget_fact534_S: string;
  budget_fact534a_3: string;
  budget_fact534a_4: string;
  budget_fact534a_5: string;
  budget_fact534a_K: string;
  changes?: string;
}

interface Region {
    code: string;
    text: string;
}

// ответ GET /api-py/ba/off-balance
interface OffBalanceApiValue {
    amount: number;
    updated_by: string;
    updated_at: string;
}

interface OffBalanceApiRow {
    row_id: number;
    row_code: string;
    name_ru: string;
    name_kk: string;
    name_en: string;
    year_start: OffBalanceApiValue | null;              // сохранённый остаток на 01.01
    report_date: OffBalanceApiValue | null;             // сохранённый остаток на дату «по»
    prefill: { amount: number; balance_date: string } | null;   // остаток на 31.12 прошлого года
    last_change: { updated_by: string; updated_at: string } | null;
}

interface OffBalanceRow {
    row_id: number;
    row_code: string;
    name_ru: string;
    name_kk: string;
    name_en: string;
    report_year_start: number | null;   // на начало отчетного года 
    report_date: number | null;         // на отчетную дату
    saved_year_start: number | null;    // сохранено в БД на 01.01 (без предзаполнения) — для поиска изменений
    saved_report_date: number | null;   // сохранено в БД на дату «по»
    prefill_date: string | null;        // ДД.ММ.ГГГГ остатка 31.12, если поле предзаполнено
    last_change: { updated_by: string; updated_at: string } | null;
}

type OffBalanceMoneyKey = 'report_year_start' | 'report_date';

// индекс вкладки «Забалансовые счета» 
const OFF_BALANCE_TAB = 2;
// модуль «Забалансовые счета»
const OFF_BALANCE_MODULE_CODE = '005.009.001';
// суммы: не более 2 знаков после запятой, ноль и отрицательные допускаются
const MONEY_INPUT_REGEX = /^-?\d*[.,]?\d{0,2}$/;
const OFF_BALANCE_MAX_DIGITS = 15;

@Component({
    components: {BIconDownload, BDropdown, DatePicker},
    mixins: [AlterationI18nMixin],
    filters: {
        formatMoney(value: unknown): unknown {
            if (typeof value !== 'number' || !Number.isFinite(value)) {
                return value;
            }
            return formatDigitsGrouping(value.toFixed(2)).replace('.', ',');
        }
    }
})


export default class BudgetAccounting extends Vue {
    setText!: (key: string) => string;

    dateFromForInput: Date = new Date();
    dateToForInput: Date = new Date();

    // приложения, по которым сейчас формируется файл (ключ — reportKey): повторная выгрузка не запускается
    downloadingReports: Record<string, boolean> = {};

    get report_list_prev() {
        return [
            {
                "id": 1,
                "name_ru": "Приложение 1",
                "name_kz": "1-қосымша",
                "description_ru": "Книга Журнал-главная",
                "description_kz": "Бас-журнал кітабы",
                "category": "МБ",
                "load_url": "ba-report-1"
            },
            {
                "id": 2,
                "name_ru": "Приложение 2",
                "name_kz": "2-қосымша",
                "description_ru": "Мемориальный ордер по единому казначейскому счету",
                "description_kz": "Бірыңғай қазынашылық шоты бойынша мемориалдық ордер",
                "category": "РБ"
            },
            {
                "id": 3,
                "name_ru": "Приложение 3",
                "name_kz": "3-қосымша",
                "description_ru": "Мемориальный ордер",
                "description_kz": "3-қосымша Мемориалдық ордер",
                "category": "МБ",
                "load_url": "ba-report-3"
            },
            {
                "id": 4,
                "name_ru": "Приложение 4",
                "name_kz": "4-қосымша",
                "description_ru": "Расшифровка по счетам",
                "description_kz": "Қолма-қол ақшаны бақылау шоттардың толық жазылуы",
                "category": "МБ",
                "load_url": "ba-report-4"
            },
            {
                "id": 5,
                "name_ru": "Приложение 5",
                "name_kz": "5-қосымша",
                "description_ru": "Расшифровка размещенных с Единого казначейского счета сумм во вклады (депозиты)",
                "description_kz": "Бірыңғай қазынашылық шоттан салымдарға (депозиттерге) орналастырылған сомалардың толық жазылуы",
                "category": "РБ"
            },
            {
                "id": 6,
                "name_ru": "Приложение 6",
                "name_kz": "6-қосымша",
                "description_ru": "Расшифровка размещенных сумм депозитов с местных бюджетов",
                "description_kz": "Жергілікті бюджеттерден депозиттерге орналастырылған сомалардың толық жазылуы",
                "category": "МБ"
            },
            {
                "id": 7,
                "name_ru": "Приложение 7",
                "name_kz": "7-қосымша",
                "description_ru": "Расшифровка по поступлениям и расходам бюджета",
                "description_kz": "Бюджеттердің түсімдері мен шығыстардың толық жазылуы",
                "category": "МБ",
                "load_url": "ba-report-7"
            },
            {
                "id": 8,
                "name_ru": "Приложение 8",
                "name_kz": "8-қосымша",
                "description_ru": "Баланс Комитета казначейства",
                "description_kz": "Қазынашылық комитетінің балансы",
                "category": "РБ"
            },
            {
                "id": 9,
                "name_ru": "Приложение 9",
                "name_kz": "9-қосымша",
                "description_ru": "Баланс исполнения местного бюджета",
                "description_kz": "Жергілікті бюджеттің атқарылу балансы",
                "category": "МБ",
                "load_url": "ba-report-9"
            },
            {
                "id": 10,
                "name_ru": "Приложение 10",
                "name_kz": "10-қосымша",
                "description_ru": "Отчет о движении денег на Едином казначейском счете",
                "description_kz": "Бірыңғай қазынашылық шоттағы ақша қозғалысы туралы есеп",
                "category": "РБ"
            },
            {
                "id": 11,
                "name_ru": "Приложение 12",
                "name_kz": "12-қосымша",
                "description_ru": "Отчет о государственном и гарантированном государством долге, долг по поручительствам государства",
                "description_kz": "Мемлекеттік және мемлекет кепілдік берген борыш, мемлекеттің кепілгерлері бойынша қарыздары туралы есеп",
                "category": "РБ"
            },
            {
                "id": 12,
                "name_ru": "Приложение 14",
                "name_kz": "14-қосымша",
                "description_ru": "Отчет по внешним займам и связанным грантам",
                "description_kz": "Сыртқы қарыздар және байланысты гранттар жөніндегі есеп",
                "category": "МБ"
            },
            {
                "id": 13,
                "name_ru": "Приложение 15",
                "name_kz": "15-қосымша",
                "description_ru": "Информация по расходованию резерва Правительства Республики Казахстан и местных исполнительных органов",
                "description_kz": "Қазақстан Республикасы Үкіметінің және жергілікті атқарушы органдар резервтерінің жұмсалуы жөніндегі ақпарат",
                "category": "МБ"
            },
            {
                "id": 14,
                "name_ru": "Приложение 16",
                "name_kz": "16-қосымша",
                "description_ru": "Сведения об остатках на счетах для учета сумм аккредитивов государственных учреждений",
                "description_kz": "Мемлекеттік мекемелердің аккредитивтер сомаларын есепке алу үшін шоттарындағы қалдықтар туралы мәліметтер",
                "category": "МБ"
            },
            {
                "id": 15,
                "name_ru": "Приложение 17",
                "name_kz": "17-қосымша",
                "description_ru": "Отчет о закрытии операционного дня по счетам в иностранной валюте",
                "description_kz": "Шетел валютасындағы шоттар бойынша операциялық күннің жабылуы туралы есеп",
                "category": "РБ"
            },
            {
                "id": 16,
                "name_ru": "Приложение 18",
                "name_kz": "18-қосымша",
                "description_ru": "Отчет о движении денег местного бюджета",
                "description_kz": "Жергілікті бюджеттің ақша қозғалысы туралы есеп",
                "category": "МБ",
                "load_url": "ba-report-18"
            },
            {
                "id": 17,
                "name_ru": "Приложение 19",
                "name_kz": "19-қосымша",
                "description_ru": "Расшифровка по счетам в иностранной валюте",
                "description_kz": "Шетел валютасының шоттардың толық жазылуы",
                "category": "МБ"
            },
            {
                "id": 18,
                "name_ru": "Приложение 20",
                "name_kz": "20-қосымша",
                "description_ru": "Отчет о закрытии операционного дня в Интегрированной информационной системе казначейства",
                "description_kz": "Қазынашылықтың интеграцияланған ақпараттық жүйесінде операциялық күннің жабылуы туралы есеп",
                "category": "РБ",
            },
            {
                "id": 19,
                "name_ru": "Приложение 21",
                "name_kz": "21-қосымша",
                "description_ru": "Информация по расходованию средств внешних займов и связанных грантов в рамках инвестиционных проектов",
                "description_kz": "Инвестициялық жобалар шеңберінде сыртқы қарыздар және байланысты гранттардың жұмсалуы бойынша ақпарат",
                "category": "МБ"
            },
            {
                "id": 20,
                "name_ru": "Приложение 23",
                "name_kz": "23-қосымша",
                "description_ru": "Справка по временно свободным бюджетным средствам (депозиты), размещенным в Национальном банке с Единого казначейского счета",
                "description_kz": "Бірыңғай қазынашылық шоттан Ұлттық банкісіне уақытша бос бюджеттік қаражаттарды (депозиттерді) орналастырылу бойынша анықтама",
                "category": "РБ"
            },
            {
                "id": 21,
                "name_ru": "Приложение 27",
                "name_kz": "27-қосымша",
                "description_ru": "Информация по кредитам, предоставленным из бюджета",
                "description_kz": "Бюджеттен берілген кредиттер бойынша ақпарат",
                "category": "МБ"
            },
            {
                "id": 22,
                "name_ru": "",
                "name_kz": "",
                "description_ru": "Оборотная ведомость",
                "description_kz": "Айналым туралы есеп",
                "category": "МБ",
                "load_url": "ba-report-trial-balance"
            }
        ]
    }

    get report_list() {
        return [
            {
                "id": 1,
                "name_ru": "Приложение 1",
                "name_kz": "1-қосымша",
                "name_ru_front": "Приложение 1",
                "description_ru": "Книга Журнал-главная",
                "description_kz": "Бас-журнал кітабы",
                "category": "МБ",
                "load_url": "ba-report-1"
            },
            {
                "id": 2,
                "name_ru": "Приложение 2",
                "name_kz": "2-қосымша",
                "name_ru_front": "Приложение 2",
                "description_ru": "Мемориальный ордер по единому казначейскому счету",
                "description_kz": "Бірыңғай қазынашылық шоты бойынша мемориалдық ордер",
                "category": "РБ"
            },
            {
                "id": 3,
                "name_ru": "Приложение 3",
                "name_kz": "3-қосымша",
                "name_ru_front": "Приложение 3",
                "description_ru": "Мемориальный ордер",
                "description_kz": "3-қосымша Мемориалдық ордер",
                "category": "МБ",
                "load_url": "ba-report-3"
            },
            {
                "id": 4,
                "name_ru": "Приложение 4",
                "name_kz": "4-қосымша",
                "name_ru_front": "Приложение 4",
                "description_ru": "Расшифровка по счетам",
                "description_kz": "Қолма-қол ақшаны бақылау шоттардың толық жазылуы",
                "category": "МБ",
                "load_url": "ba-report-4"
            },
            {
                "id": 5,
                "name_ru": "Приложение 5",
                "name_kz": "5-қосымша",
                "name_ru_front": "Приложение 5",
                "description_ru": "Расшифровка размещенных с Единого казначейского счета сумм во вклады (депозиты)",
                "description_kz": "Бірыңғай қазынашылық шоттан салымдарға (депозиттерге) орналастырылған сомалардың толық жазылуы",
                "category": "РБ"
            },
            {
                "id": 6,
                "name_ru": "Приложение 6",
                "name_kz": "6-қосымша",
                "name_ru_front": "Приложение 6",
                "description_ru": "Расшифровка по поступлениям и расходам бюджета",
                "description_kz": "Бюджеттердің түсімдері мен шығыстардың толық жазылуы",
                "category": "МБ",
                "load_url": "ba-report-7"
            },
            {
                "id": 7,
                "name_ru": "Приложение 8",
                "name_kz": "8-қосымша",
                "name_ru_front": "Приложение 7",
                "description_ru": this.$t("modules.ba.apps.app_7"),
                "description_kz": "Мемлекеттік қазынашылықтың № 1 нысан бойынша балансы",
                "category": "РБ" // was Appendix 8 prior to 2026
            },
            {
                "id": 8,
                "name_ru": "Приложение 8",
                "name_kz": "8-қосымша",
                "name_ru_front": "Приложение 8",
                "description_ru": "Баланс исполнения местного бюджета по форме №1-М ",
                "description_kz": "Жергілікті бюджеттің атқарылу балансы",
                "category": "МБ",
                "load_url": "ba-report-9"
            },
            {
                "id": 9,
                "name_ru": "Приложение 10",  // what appendix needs to be downloded
                "name_kz": "9-қосымша",
                "name_ru_front": "Приложение 9", // what we show on frontend
                "description_ru": "Отчет о движении денег на Едином казначейском счете по форме №2",
                "description_kz": "Бірыңғай қазынашылық шоттағы ақша қозғалысы туралы есеп",
                "category": "РБ" // was Appendix 10 prior to 2026
            },
            {
                "id": 10,
                "name_ru": "Приложение 12",
                "name_kz": "12-қосымша",
                "name_ru_front": "Приложение 10",
                "description_ru": "Отчет о государственном и гарантированном государством долге, долг по поручительствам государства по форме №3",
                "description_kz": "Мемлекеттік және мемлекет кепілдік берген борыш, мемлекеттің кепілгерлері бойынша қарыздары туралы есеп",
                "category": "РБ" // as Appendix 12 prior to 2026
            },
            {
                "id": 11,
                "name_ru": "Приложение 14",
                "name_kz": "14-қосымша",
                "name_ru_front": "Приложение 11",
                "description_ru": "Отчет по внешним займам и связанным грантам по форме №5",
                "description_kz": "Сыртқы қарыздар және байланысты гранттар жөніндегі есеп",
                "category": "МБ" // was appendix 14
            },
            {
                "id": 12,
                "name_ru": "Приложение 15",
                "name_kz": "15-қосымша",
                "name_ru_front": "Приложение 12",
                "description_ru": "Информация по расходованию резерва Правительства Республики Казахстан и местных исполнительных органов",
                "description_kz": "Қазақстан Республикасы Үкіметінің және жергілікті атқарушы органдар резервтерінің жұмсалуы жөніндегі ақпарат",
                "category": "МБ" // was appendix 15
            },
            {
                "id": 13,
                "name_ru": "Приложение 16",
                "name_kz": "16-қосымша",
                "name_ru_front": "Приложение 13",
                "description_ru": "Сведения об остатках на счетах для учета сумм аккредитивов государственных учреждений",
                "description_kz": "Мемлекеттік мекемелердің аккредитивтер сомаларын есепке алу үшін шоттарындағы қалдықтар туралы мәліметтер",
                "category": "МБ" // was appendix 16
            },
            {
                "id": 14,
                "name_ru": "Приложение 17",
                "name_kz": "14-қосымша",
                "name_ru_front": "Приложение 14",
                "description_ru": "Отчет о закрытии операционного дня по счетам в иностранной валюте по форме №6",
                "description_kz": "Шетел валютасындағы шоттар бойынша операциялық күннің жабылуы туралы есеп",
                "category": "РБ" // was appendix 17
            },
            {
                "id": 15,
                "name_ru": "Приложение 15",
                "name_kz": "15-қосымша",
                "name_ru_front": "Приложение 15",
                "description_ru": "Отчет о движении денег местного бюджета по форме №2-М",
                "description_kz": "Жергілікті бюджеттің ақша қозғалысы туралы есеп",
                "category": "МБ",
                "load_url": "ba-report-18"
            },
            {
                "id": 16,
                "name_ru": "Приложение 19",
                "name_kz": "19-қосымша",
                "name_ru_front": "Приложение 16",
                "description_ru": "Расшифровка по счетам в иностранной валюте",
                "description_kz": "Шетел валютасының шоттардың толық жазылуы",
                "category": "МБ" // was appendix 19
            },
            {
                "id": 17,
                "name_ru": "Приложение 20",
                "name_kz": "20-қосымша",
                "name_ru_front": "Приложение 17",
                "description_ru": "Отчет о закрытии операционного дня в Интегрированной информационной системе казначейства",
                "description_kz": "Қазынашылықтың интеграцияланған ақпараттық жүйесінде операциялық күннің жабылуы туралы есеп",
                "category": "РБ", // was appendix 20
            },
            {
                "id": 18,
                "name_ru": "Приложение 21",
                "name_kz": "21-қосымша",
                "name_ru_front": "Приложение 18",
                "description_ru": "Информация по расходованию средств внешних займов и связанных грантов в рамках инвестиционных проектов",
                "description_kz": "Инвестициялық жобалар шеңберінде сыртқы қарыздар және байланысты гранттардың жұмсалуы бойынша ақпарат",
                "category": "МБ" // was appendix 21
            },
            {
                "id": 19,
                "name_ru": "Приложение 23",
                "name_kz": "23-қосымша",
                "name_ru_front": "Приложение 19",
                "description_ru": this.$t("modules.ba.apps.app_19"),
                "category": "РБ" // was appendix 23
            },
            {
                "id": 20,
                "name_ru": "Приложение 27",
                "name_kz": "27-қосымша",
                "name_ru_front": "Приложение 20",
                "description_ru": "Информация по кредитам, предоставленным из бюджета",
                "description_kz": "Бюджеттен берілген кредиттер бойынша ақпарат",
                "category": "МБ" // was appendix 27
            },
            {
                "id": 21,
                "name_ru": "",
                "name_kz": "",
                "description_ru": "Оборотная ведомость",
                "description_kz": "Айналым туралы есеп",
                "category": "МБ",
                "load_url": "ba-report-trial-balance"
            }
        ]
    }

    get report_fields() {
        return [
            {key: "id", label: "№ П/П", sortable: false},
            {key: "load", label: "Скачать", sortable: false},
            {key: this.dateToForInput.getFullYear() < 2026 ? "name_ru" : "name_ru_front", label: "Наименование файла", sortable: false},
            {key: "description_ru", label: "Описание файла", sortable: false},
        ]
    }

    currDate = new Date();
    regionBase = []

    status_fields = [
        { key: "date", label: "Дата", sortable: false, thStyle: 'width: 120px' },
        { key: "budget_fact520_data", label: "Форма 5-20", sortable: false, thStyle: 'width: 200px' },
        { key: "budget_fact534_1", label: "Форма 5-34(FS)", sortable: false, thStyle: 'width: 200px' },
        { key: "budget_fact534_G", label: "Форма 5-34(G)", sortable: false, thStyle: 'width: 200px' },
        { key: "budget_fact534_S", label: "Форма 5-34(S)", sortable: false, thStyle: 'width: 200px' },
        { key: "budget_fact534a_3", label: "Форма 5-34А (Источник финансирования – 3)", sortable: false, thStyle: 'width: 200px' },
        { key: "budget_fact534a_4", label: "Форма 5-34А (Источник финансирования – 4)", sortable: false, thStyle: 'width: 200px' },
        { key: "budget_fact534a_5", label: "Форма 5-34А (Источник финансирования – 5)", sortable: false, thStyle: 'width: 200px' },
        { key: "budget_fact534a_K", label: "Форма 5-34А (Источник финансирования – K)", sortable: false, thStyle: 'width: 200px' },
    ];

    statusRows: any[] = [];

    // off-balance accounts table fields 
    get off_balance_fields() {
        return [
            { key: "form_row", label: this.setText('budget_accounting.form_row'), sortable: false, thStyle: 'width: 8%' },
            { key: "name", label: this.setText('budget_accounting.name'), sortable: false, thStyle: 'width: 24%' },
            { key: "report_year_start", label: this.setText('budget_accounting.report_year_start'), sortable: false, thClass: 'text-right', thStyle: 'width: 21%' },
            { key: "report_date", label: this.setText('budget_accounting.report_date'), sortable: false, thClass: 'text-right', thStyle: 'width: 21%' },
            { key: "date", label: this.setText('budget_accounting.date'), sortable: false, thStyle: 'width: 13%' },
            { key: "user", label: this.setText('budget_accounting.user'), sortable: false, thStyle: 'width: 13%' },
        ];
    }

    // Строки показываются только после «Применить» (до этого — подсказка)
    // Сохранение идёт по региону и дате, с которыми таблица загружена, 
    // а не по текущему (не применённому) фильтру.
    offBalanceApplied = false;
    offBalanceRegion = '';
    offBalanceDateTo = '';    // ГГГГ-ММ-ДД
    // для хлебных крошек: последний успешно применённый фильтр 
    // (отклонённый «Применить» их не меняет)
    offBalanceRegionText = '';
    offBalanceDateFrom = '';                   // ГГГГ-ММ-ДД
    offBalanceData: OffBalanceApiRow[] = [];   // последний ответ сервера — из него восстанавливаются строки
    offBalanceRows: OffBalanceRow[] = [];

    async loadOffBalance() {
        if (!this.realmUsers.length) {
            await this.loadRealmUsers(); 
        }
        try {
            const response = await axios.get('/api-py/ba/off-balance', {
                params: { region: this.offBalanceRegion, date_to: this.offBalanceDateTo },
            });
            this.offBalanceData = response.data;
            this.buildOffBalanceRows();
        } catch (error) {
            const err = error as Error;
            console.error('Ошибка загрузки забалансовых счетов:', err);
            this.makeToast('danger', this.setText('budget_accounting.load_error'), this.setText('budget_accounting.loading'));
        }
    }

    // строки для редактирования из последнего ответа сервера
    buildOffBalanceRows() {
        this.offBalanceInvalid = {};
        this.offBalanceRows = this.offBalanceData.map(r => {
            const savedYearStart = r.year_start ? r.year_start.amount : null;
            // остаток на 01.01 не сохранён
            // предзаполняем остатком на 31.12 прошлого года
            const prefill = savedYearStart === null ? r.prefill : null;
            return {
                row_id: r.row_id,
                row_code: r.row_code,
                name_ru: r.name_ru,
                name_kk: r.name_kk,
                name_en: r.name_en,
                report_year_start: prefill ? prefill.amount : savedYearStart,
                report_date: r.report_date ? r.report_date.amount : null,
                saved_year_start: savedYearStart,
                saved_report_date: r.report_date ? r.report_date.amount : null,
                prefill_date: prefill ? this.isoToLocal(prefill.balance_date) : null,
                last_change: r.last_change,
            };
        });
    }

    offBalanceName(row: OffBalanceRow): string {
        const name = (row as any)[`name_${this.$i18n.locale}`];
        return name || row.name_ru;
    }

    // «2026-06-30…» → «30.06.2026» (без перевода в Date)
    isoToLocal(value: string | null | undefined): string {
        if (!value) {
            return '';
        }
        return `${value.slice(8, 10)}.${value.slice(5, 7)}.${value.slice(0, 4)}`;
    }

    // «Пользователь»
    realmUsers: any[] = [];

    async loadRealmUsers() {
        try {
            const response = await axios.get('/api-py/get-realm-users/', {
                headers: { Authorization: `Bearer ${this.$store.getters.token}` },
            });
            this.realmUsers = response.data || [];
        } catch (error) {
            console.error('Ошибка загрузки пользователей:', error);
        }
    }

    userFullName(userId: string | null | undefined): string {
        const user = this.realmUsers.find((u: any) => u.id === userId);
        if (!user) {
            return '';
        }
        return [user.lastName, user.firstName].filter(Boolean).join(' ');
    }

    // Ввод сумм: поле
    moneyEditKey: string | null = null;   // `${row_id}:${поле}` редактируемой ячейки
    moneyEditValue = '';

    moneyCellKey(row: OffBalanceRow, key: OffBalanceMoneyKey): string {
        return `${row.row_id}:${key}`;
    }

    // поля с неверным форматом: ключ ячейки → введённый текст (остаётся в поле)
    offBalanceInvalid: Record<string, string> = {};

    isMoneyInvalid(row: OffBalanceRow, key: OffBalanceMoneyKey): boolean {
        return this.moneyCellKey(row, key) in this.offBalanceInvalid;
    }

    moneyCellValue(row: OffBalanceRow, key: OffBalanceMoneyKey): number | string | null {
        const cellKey = this.moneyCellKey(row, key);
        if (this.moneyEditKey === cellKey) {
            return this.moneyEditValue;
        }
        return cellKey in this.offBalanceInvalid ? this.offBalanceInvalid[cellKey] : row[key];
    }

    onMoneyFocus(row: OffBalanceRow, key: OffBalanceMoneyKey) {
        const cellKey = this.moneyCellKey(row, key);
        this.moneyEditKey = cellKey;
        if (cellKey in this.offBalanceInvalid) {
            this.moneyEditValue = this.offBalanceInvalid[cellKey];
        } else {
            this.moneyEditValue = row[key] === null ? '' : String(row[key]);
        }
    }

    // при вводе: поле очищено или значение уже в верном формате
    // подсветка ошибки снимается сразу
    onMoneyInput(row: OffBalanceRow, key: OffBalanceMoneyKey, value: string) {
        this.moneyEditValue = value;
        const cellKey = this.moneyCellKey(row, key);
        if (cellKey in this.offBalanceInvalid && this.parseMoney(value) !== undefined) {
            this.$delete(this.offBalanceInvalid, cellKey);
        }
    }

    onMoneyBlur(row: OffBalanceRow, key: OffBalanceMoneyKey) {
        const cellKey = this.moneyCellKey(row, key);
        const parsed = this.parseMoney(this.moneyEditValue);
        if (parsed === undefined) {
            // неверный формат: текст остаётся в поле, значение строки не меняется
            this.$set(this.offBalanceInvalid, cellKey, this.moneyEditValue);
        } else {
            row[key] = parsed;
            this.$delete(this.offBalanceInvalid, cellKey);
        }
        this.moneyEditKey = null;
        this.moneyEditValue = '';
    }

    parseMoney(value: string): number | null | undefined {
        const normalized = value.replace(/\s/g, '').replace(',', '.');
        if (normalized === '') {
            return null;
        }
        if (!/^-?(\d+\.?\d{0,2}|\.\d{1,2})$/.test(normalized)) {
            return undefined;
        }
        const [intPart, fracPart = ''] = normalized.replace('-', '').split('.');
        const significant = intPart.replace(/^0+/, '') + fracPart.replace(/0+$/, '');
        if (significant.length > OFF_BALANCE_MAX_DIGITS) {
            return undefined;
        }
        const num = Number(normalized);
        return Number.isFinite(num) ? rounding(num, 2) : undefined;
    }

    // права на редактирование
    get offBalanceEditable(): boolean {
        const userModules: any[] = this.$store.state.user?.userModules || [];
        const link = userModules.find((m: any) => m.modules === OFF_BALANCE_MODULE_CODE);
        return !!link && link.access_level >= 2;
    }

    offBalanceSaving = false;

    // есть ли отличия от сохранённого в БД; 
    // предзаполнение из 31.12 — тоже изменение (записи на 01.01 ещё нет)
    hasOffBalanceChanges(): boolean {
        return this.offBalanceRows.some(r =>
            r.report_year_start !== r.saved_year_start || r.report_date !== r.saved_report_date
        );
    }

    async saveOffBalance() {
        if (!this.offBalanceEditable) {
            return;
        }
        // период в фильтре неверный — не сохраняем, то же уведомление, что при «Применить»
        if (this.isOffBalancePeriodInvalid()) {
            this.showOffBalancePeriodError();
            return;
        }
        if (Object.keys(this.offBalanceInvalid).length) {
            this.showOffBalanceSaveError();
            return;
        }
        // сохранять нечего (например, все поля пустые и ничего не сохранено) — ничего не делаем
        if (!this.hasOffBalanceChanges()) {
            return;
        }
        this.offBalanceSaving = true;
        try {
            await axios.post('/api-py/ba/off-balance', {
                region: this.offBalanceRegion,
                date_to: this.offBalanceDateTo,
                user_id: this.$store.state.user.sub,
                // предзаполненное из 31.12 значение уходит как остаток на 01.01
                rows: this.offBalanceRows.map(r => ({
                    row_id: r.row_id,
                    year_start: r.report_year_start,
                    report_date: r.report_date,
                })),
            });
            this.makeToast('success', this.setText('budget_accounting.save_success'), this.$t('modules.common.saving') as string);
            await this.loadOffBalance();
        } catch (error) {
            const fields = axios.isAxiosError(error) && error.response && error.response.status === 400
                ? error.response.data?.detail?.fields
                : null;
            if (Array.isArray(fields)) {
                // сервер отклонил формат: подсвечиваем поля, текст в них — как отображается сейчас
                fields.forEach((f: { row_id: number; field: string }) => {
                    const key: OffBalanceMoneyKey = f.field === 'year_start' ? 'report_year_start' : 'report_date';
                    const row = this.offBalanceRows.find(r => r.row_id === f.row_id);
                    if (row) {
                        this.$set(this.offBalanceInvalid, this.moneyCellKey(row, key), row[key] === null ? '' : String(row[key]));
                    }
                });
                this.showOffBalanceSaveError();
            } else if (axios.isAxiosError(error) && error.response && error.response.status === 403) {
                // сервер: нет прав на редактирование
                this.makeToast('danger', this.setText('budget_accounting.no_edit_rights'), this.$t('modules.common.saving') as string);
            } else {
                const err = error as Error;
                console.error('Ошибка сохранения забалансовых счетов:', err);
                this.makeToast('danger', this.setText('budget_accounting.save_failed'), this.$t('modules.common.saving') as string);
            }
        } finally {
            this.offBalanceSaving = false;
        }
    }

    showOffBalanceSaveError() {
        this.$bvModal.msgBoxOk(this.setText('budget_accounting.save_error'), {
            title: this.setText('budget_accounting.save_error_title'),
            okTitle: this.setText('budget_accounting.ok'),
            centered: true,
        });
    }

    // не даёт ввести символ, после которого значение перестанет соответствовать MONEY_INPUT_REGEX
    onMoneyKeypress(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            (event.target as HTMLInputElement).blur();
            return;
        }
        if (event.ctrlKey || event.metaKey || event.key.length !== 1) {
            return;
        }
        const input = event.target as HTMLInputElement;
        const start = input.selectionStart ?? 0;
        const end = input.selectionEnd ?? 0;
        const nextValue = input.value.substring(0, start) + event.key + input.value.substring(end);
        if (!MONEY_INPUT_REGEX.test(nextValue)) {
            event.preventDefault();
        }
    }

    async mounted() {
        await this.getObl();
        await this.loadRegions();
        this.setDefaultRegion();
        this.resetFilterDates();
        await this.loadFormStatuses();
    }

            async loadFormStatuses() {
                try {
                    const dateFrom = this.formatDate(this.dateFromForInput);
                    const dateTo = this.formatDate(this.dateToForInput);
                    const selectedRegion = this.currRegion?.code || '';

                    const queryParams = new URLSearchParams({
                        date_from: dateFrom,
                        date_to: dateTo,
                    });

                    if (selectedRegion) {
                        queryParams.append("region", selectedRegion);
                    }

                    const response = await fetch(`/api-py/ba/form-upload-status?${queryParams.toString()}`);
                    const rawData = await response.json();
                    console.log('RAW SERVER RESPONSE:', rawData);

                    this.statusRows = rawData.map((row: any) => {
                        const rawStatuses = {
                            budget_fact520_data: row.budget_fact520_data,
                            budget_fact534_1: row.budget_fact534_1,
                            budget_fact534_G: row.budget_fact534_G,
                            budget_fact534_S: row.budget_fact534_S,
                            budget_fact534a_3: row.budget_fact534a_3,
                            budget_fact534a_4: row.budget_fact534a_4,
                            budget_fact534a_5: row.budget_fact534a_5,
                            budget_fact534a_K: row.budget_fact534a_K,
                        };

                        const notLoadedFields = Object.keys(rawStatuses).filter(
                            key => rawStatuses[key as keyof typeof rawStatuses] === 'Не загружен'
                        );

                        return {
                            ...row,
                            raw_status: rawStatuses,
                            budget_fact520_data: this.getStatusTag(row.budget_fact520_data),
                            budget_fact534_1: this.getStatusTag(row.budget_fact534_1),
                            budget_fact534_G: this.getStatusTag(row.budget_fact534_G),
                            budget_fact534_S: this.getStatusTag(row.budget_fact534_S),
                            budget_fact534a_3: this.getStatusTag(row.budget_fact534a_3),
                            budget_fact534a_4: this.getStatusTag(row.budget_fact534a_4),
                            budget_fact534a_5: this.getStatusTag(row.budget_fact534a_5),
                            budget_fact534a_K: this.getStatusTag(row.budget_fact534a_K),
                            notLoadedCount: notLoadedFields.length,
                            showAddInField: null,
                        };
                    });
                } catch (error) {
                    console.error('Ошибка загрузки статуса форм:', error);
                }
    }


    getStatusTag(status: string): string {
        if (status === 'Загружен') {
            return `<span style="display: block; width: 100%; background-color: #d4edda; color: green; padding: 10px 0; text-align: center;">Загружен</span>`;
        } else {
            return `<span style="display: block; width: 100%; background-color: #f8d7da; color: red; padding: 10px 0; text-align: center;">Не загружен</span>`;
        }
    }

    resetFilterDates() {
        this.dateFromForInput = new Date(`${new Date().getFullYear()}-${new Date().getMonth() + 1}` + '-01');
        console.log('dateFromForInput:', this.dateFromForInput);
        this.dateToForInput = new Date(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-${this.getLastDayOfMonth(new Date().getMonth() + 1, new Date().getDate())}`)
    }

    getLastDayOfMonth(year: number, month: number) {
            const date = new Date(year, month + 1, 0);
            return date.getDate();
    }

    formatDate(date: Date): string {
        const yy = date.getFullYear().toString(); //
        const mm = String(date.getMonth() + 1).padStart(2, '0'); // Месяцы начинаются с 0, добавляем ведущий 0
        const dd = String(date.getDate()).padStart(2, '0'); // Добавляем ведущий 0
        return `${yy}-${mm}-${dd}`;
    }

    formatDateLocal(date: Date): string {
        const yy = date.getFullYear().toString(); //
        const mm = String(date.getMonth() + 1).padStart(2, '0'); // Месяцы начинаются с 0, добавляем ведущий 0
        const dd = String(date.getDate()).padStart(2, '0'); // Добавляем ведущий 0
        return `${dd}.${mm}.${yy}`;
    }

    // ключ приложения: load_url, для строк без него — name_ru (в списке 2026 name_ru повторяются у строк с load_url)
    reportKey(row: any): string {
        return row.load_url || row.name_ru;
    }

    isReportDownloading(row: any): boolean {
        return !!this.downloadingReports[this.reportKey(row)];
    }

    downloadNoticeId(key: string): string {
        return 'ba-download-' + key;
    }

    // уведомление «Началась загрузка…»: висит, пока файл не отдан или не пришла ошибка
    showDownloadNotice(key: string) {
        const id = this.downloadNoticeId(key);
        // повторное нажатие: показать заново, чтобы пользователь заметил
        this.$bvToast.hide(id);
        this.$nextTick(() => {
            // выгрузка уже завершилась — не оставлять висящее уведомление
            if (!this.downloadingReports[key]) {
                return;
            }
            this.$bvToast.toast(this.setText('budget_accounting.download_started'), {
                id: id,
                title: this.setText('budget_accounting.download_notice_title'),
                variant: 'success',
                noAutoHide: true,
                solid: true
            });
        });
    }

    hideDownloadNotice(key: string) {
        this.$bvToast.hide(this.downloadNoticeId(key));
    }

    // одно нажатие — один файл: блокировка по приложению на время формирования
    async runReportDownload(key: string, download: () => Promise<void>) {
        this.$set(this.downloadingReports, key, true);
        this.showDownloadNotice(key);
        try {
            await download();
        } finally {
            this.$delete(this.downloadingReports, key);
            this.hideDownloadNotice(key);
        }
    }

    async load_report(row: any) {
        const key = this.reportKey(row);
        if (this.downloadingReports[key]) {
            this.showDownloadNotice(key);
            return;
        }

        let region: string = "330101"; // Default value
        if (this.currRegion !== null) {
            region = this.currRegion.code; // No more "never" error
        }
        const formattedDateFrom = this.formatDate(this.dateFromForInput);
        const formattedDateTo = this.formatDate(this.dateToForInput);

        const dateFrom = new Date(this.dateFromForInput);
        const dateTo = new Date(this.dateToForInput);

        // Проверка периодичности для Приложений 4,7,18
        const split_url = row.load_url.split('-');
        const endPart = split_url.pop();
        const secEndPart = split_url.pop();

        if (!this.isSameCalendarYear(dateFrom, dateTo)) {
            if (endPart === '4') {
                this.doNotificationForAppendix('4');
                return;
            } else if (endPart === '7') {
                this.doNotificationForAppendix('7');
                return;
            } else if (endPart == '18') {
                this.doNotificationForAppendix('18');
                return;
            }
            // Проверка периодичности для Оборотной ведомости
            if (endPart === 'balance' && secEndPart === 'trial') {
                this.doNotificationForTrialBalance();
                return;
            }
        }

        // Проверка периодичности для Приложения 9
        if (endPart === '9') {
            if (!this.isValidHalfOrFullYear(dateFrom, dateTo)) {
                this.doNotificationForAppendix9();
                return;
            }
        }

        const file_name = this.dateToForInput.getFullYear() < 2026 ? row.name_ru : row.name_ru_front;
        await this.runReportDownload(key, () =>
            this.download_report(row.load_url + '/' + region + '?date_from=' + formattedDateFrom + "&" + "date_to=" + formattedDateTo + "&" + "lang=" + this.$i18n.locale, file_name + '(' + this.formatDate(this.currDate) + ')')
        );
    }

    isSameCalendarYear(dateFrom: Date, dateTo: Date): boolean {
        return dateFrom.getFullYear() === dateTo.getFullYear();
    }

    isValidHalfOrFullYear(dateFrom: Date, dateTo: Date): boolean {
        const sameYear = dateFrom.getFullYear() === dateTo.getFullYear();
        const isJan1 = dateFrom.getMonth() === 0 && dateFrom.getDate() === 1;
        const isJune30 = dateTo.getMonth() === 5 && dateTo.getDate() === 30;
        const isDec31 = dateTo.getMonth() === 11 && dateTo.getDate() === 31;

        return sameYear && isJan1 && (isJune30 || isDec31);
    }


    async doNotificationForAppendix(appendixId: string) {
        const dict: Record<string, { ru: string; kk: string; en: string }>  = {
            '4': {
                'ru': "Расшифровка по счетам",
                'kk': "Шоттар бойынша толық жазылу",
                'en': "Breakdown of accounts"
            },
            '7': {
                'ru': "Расшифровка по поступлениям и расходам бюджета",
                'kk': "Бюджеттердің түсімдері мен шығыстардың толық жазылуы",
                'en': "Breakdown of budget receipts and expenditures"
            },
            '18': {
                'ru': "Отчет о движении денег местного бюджета",
                'kk': "Жергілікті бюджеттің ақша қозғалысы туралы есеп",
                'en': "Local budget cash flow report"
            }
        };

        const appendixName = dict[appendixId]?.[this.$i18n.locale as 'ru' | 'kk' | 'en'] ?? '';

        if (this.$i18n.locale == 'kk') {
            this.makeToast(
                "danger",
                `Таңдалған күндер аралығы Қосымша ${appendixId} «${appendixName}» \
                құжатын қалыптастыру үшін қолжетімді емес. Рұқсат етілетін кезең — тек күнтізбелік жыл.`,
                "Хабарлама"
            );
        } else if (this.$i18n.locale == 'ru') {
            this.makeToast(
                "danger",
                `Выбранный диапазон дат недоступен для формирования Приложения ${appendixId} \
                «${appendixName}». Допустимая периодичность — только календарный год.`,
                "Хабарлама"
            );
        } else if (this.$i18n.locale == 'en') {
            this.makeToast(
                "danger",
                `The selected date range is not available for generating Appendix ${appendixId} \
                «${appendixName}». The allowed period is only the same calendar year.`,
                "Notification"
            );
        }
    }

    async doNotificationForAppendix9() {
        if (this.$i18n.locale == 'kk') {
            this.makeHtmlToast(
                "danger",
                "Таңдалған күндер ауқымы 9-қосымша «Жергілікті бюджеттің атқарылуы балансын» құру \
                үшін қолжетімді емес. Рұқсат етілетін кезең — <strong>тек жартыжылдық немесе толық күнтізбелік жыл</strong>, 1 қаңтардан басталуы қажет.",
                "Хабарлама"
            );
        } else if (this.$i18n.locale == 'ru') {
            this.makeHtmlToast(
                "danger",
                "Выбранный диапазон дат недоступен для формирования Приложения 9 «Баланс исполнения местного бюджета». Допустимая периодичность — <strong>только полугодие или календарный год</strong>, начиная с 1 января.",
                "Уведомление"
            );
        } else if (this.$i18n.locale == 'en') {
            this.makeHtmlToast(
                "danger",
                "The selected date range is not available for generating Appendix 9 “Local Budget Execution Balance”. The allowed periods are <strong>only half-year or full calendar year</strong>, starting from January 1.",
                "Notification"
            )
        }
    }

    async doNotificationForTrialBalance() {
        if (this.$i18n.locale == 'kk') {
            this.makeToast(
                "danger",
                "Таңдалған күндер диапазоны бойынша Айналым теңгерімін қалыптастыру мүмкін емес. Рұқсат етілген кезең — тек күнтізбелік жыл.",
                "Хабарлама"
            );
        } else if (this.$i18n.locale == 'ru') {
            this.makeToast(
                "danger",
                "Выбранный диапазон дат недоступен для формирования Оборотной ведомости. Допустимая периодичность — только календарный год.",
                "Уведомление"
            );
        } else if (this.$i18n.locale == 'en') {
            this.makeToast(
                "danger",
                "The selected date range is not available for generating Turnover balance. The allowed period is only the same calendar year.",
                "Notification"
            )
        }
    }

    async load_report_by_name(row: any) {
        const key = this.reportKey(row);
        if (this.downloadingReports[key]) {
            this.showDownloadNotice(key);
            return;
        }

        if (row.category == "РБ") {
            this.do_notification();
        }

        const file_name = this.dateToForInput.getFullYear() < 2026 ? row.name_ru : row.name_ru_front;
        await this.runReportDownload(key, () =>
            this.download_report_by_name("/ba-report/" + row.name_ru + '/' + this.formatDate(this.currDate), file_name + '(' + this.formatDate(this.currDate) + ')')
        );
    }

    obl = null;

    async getObl() {
        try {
            await fetch('/api-py/get-budget-obl/' + this.$store.state._instanceCode)
                .then(response => response.json())
                .then(json => {
                    this.obl = json.obl;
                    // this.region = json.region;
                });
        } catch (error) {
        const err = error as Error;
        this.makeToast('danger', 'Ошибка запроса getObl', err.message || err.toString());
        }

    }


    currRegion: Region | null = null; // Define the type explicitly

    async loadRegions() {
        try {
        const response = await fetch('/api-py/get-user-regions-by-obl/' + this.obl + '/' + this.$store.getters.user_uuid);
        this.regionBase = await response.json();

        console.log('regionBase:', this.regionBase);
        } catch (error) {
        const err = error as Error;
        this.makeToast('danger', 'Ошибка запроса getObl', err.message || err.toString());
        }
    }

    setDefaultRegion() {
        if (this.regionBase.length === 0) return;

        this.currRegion = this.regionBase.reduce<Region>((min: Region, curr: Region) => {
            const currCodeNum = parseInt(curr.code, 10);
            const minCodeNum = parseInt(min.code, 10);

            if (isNaN(currCodeNum)) return min;
            if (isNaN(minCodeNum)) return curr;

            return currCodeNum < minCodeNum ? curr : min;
        }, this.regionBase[0]);
    }



    async download_report_by_name(url: string, name: string) {
        try {
            const response = await axios.get(`/api-py/ba/${url}`, {responseType: 'blob'});
            const blob = new Blob([response.data]);
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', name + '.xls');
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(link);
        } catch (error) {
            console.log('download_report_by_name error:', error);
            this.makeToast(
                'danger',
                this.setText('budget_accounting.download_error'),
                this.setText('budget_accounting.download_error_title')
            );
        }

    }

    async do_notification() {
        if (this.$i18n.locale == 'kk') {
            this.makeToast(
                "warning",
                "Назар аударыңыз! Бұл қосымша Республикалық Бюджет деңгейінде қалыптастырылады.",
                "Хабарлама"
            );
        } else {
            this.makeToast(
                "warning",
                "Обратите внимание! Данное приложение формируется на уровне Республиканского Бюджета.",
                "Уведомление"
            );
        }
    }


    async download_report(url: string, name: string) {
        try {
            const response = await axios.get(`/api-py/ba/${url}`, {responseType: 'blob'});
            const blob = new Blob([response.data]);
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;

            // наименование файла Оборотной ведомости
            const firstPart = url.split('/')[0];
            if (firstPart === 'ba-report-trial-balance') {
                if (this.$i18n.locale == 'ru') {
                    name = `Оборотная ведомость (${this.formatDate(this.currDate)})`;
                } else if (this.$i18n.locale == 'kk') {
                    name = `Айналым теңгерімі (${this.formatDate(this.currDate)})`;
                } else if (this.$i18n.locale == 'en') {
                    name = `Turnover balance (${this.formatDate(this.currDate)})`;
                }
            }

            link.setAttribute('download', name + '.xls');
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(link);
        } catch (error) {
            if (axios.isAxiosError(error) && error.response && error.response.status === 400) {
                try {
                    const text = await (error.response.data as Blob).text();
                    const data = JSON.parse(text);
                    if (data?.detail === "Invalid date range") {
                        // catch date range validation error for appendices 4,7,18,9, trial balance from backend
                        const match = url.match(/ba-report-([^/?]+)/);
                        if (match && match[1] === '4') {
                            this.doNotificationForAppendix('4');
                            return;
                        } else if (match && match[1] === '7') {
                            this.doNotificationForAppendix('7');
                            return;
                        } else if (match && match[1] === '18') {
                            this.doNotificationForAppendix('18');
                            return;
                        } else if (match && match[1] === 'trial-balance') {
                            this.doNotificationForTrialBalance();
                            return;
                        } else if (match && match[1] === '9') {
                            this.doNotificationForAppendix9();
                            return;
                        }
                    }
                } catch (parseError) {
                    console.error("Failed to parse error response", parseError);
                }
            }

            const err = error as Error;
            this.makeToast('danger', 'Ошибка запроса getObl', err.message || err.toString());
        }
    }

     makeHtmlToast(variant: string, htmlBody: string, title: string) {
        const h = this.$createElement;
        const htmlVNode = h('div', {
            domProps: { innerHTML: htmlBody }
        });

        this.$bvToast.toast(htmlVNode, {
            title: title,
            variant: variant,
            autoHideDelay: 4000,
            solid: true
        });
    }


    makeToast(variant: string, tostbody: string, title: string): void {
        this.$bvToast.toast(tostbody, {
            title: title,
            variant: variant,
            autoHideDelay: 2000,
            solid: true
        });
    }

    $refs!: {
        drop: any;
        filterReg: any;
        filterDate: any;
    };

    openFilterByRef(refName: string) {
        const drop = this.$refs.drop;
        drop.show(true);
        this.$emit('openFilterByRef', refName);
        let refItem: any;
        switch (refName) {
            case 'filterReg':
                refItem = this.$refs.filterReg;
                break;
            case 'filterDate':
                refItem = this.$refs.filterDate;
                break;
            default:
                break;
        }
        setTimeout(() => refItem.$el.focus(), 100);
    }

    resetFilter() {
        this.currDate = new Date();
        this.setDefaultRegion();
        this.resetFilterDates();
    }

    onRegionChange(region: Region) {
        this.currRegion = region;
        if (this.currDate && region) {
            this.loadFormStatuses();
        }
    }

    onClickApply() {
        // «Забалансовые счета»: проверка периода и загрузка таблицы — только на этой вкладке
        if (this.activeTab === OFF_BALANCE_TAB && !this.applyOffBalanceFilter()) {
            return;
        }
        this.loadFormStatuses();
        this.$refs.drop.hide(true);
    }

    // дата «с» позже даты «по» (или не задана) — период в фильтре неверный
    isOffBalancePeriodInvalid(): boolean {
        return !this.dateFromForInput || !this.dateToForInput
            || this.formatDate(this.dateFromForInput) > this.formatDate(this.dateToForInput);
    }

    showOffBalancePeriodError() {
        this.$bvModal.msgBoxOk(this.setText('budget_accounting.period_error'), {
            title: this.setText('budget_accounting.period_error_title'),
            okTitle: this.setText('budget_accounting.ok'),
            centered: true,
        });
    }

    // хлебные крошки вкладки: после «Применить» — применённый фильтр (по нему и таблица, и сохранение),
    // до первого «Применить» — текущие значения фильтра
    get offBalanceCrumbRegion(): string {
        return this.offBalanceApplied ? this.offBalanceRegionText : (this.currRegion?.text || '');
    }

    get offBalanceCrumbPeriod(): string {
        if (this.offBalanceApplied) {
            return `${this.isoToLocal(this.offBalanceDateFrom)} - ${this.isoToLocal(this.offBalanceDateTo)}`;
        }
        if (!this.dateFromForInput || !this.dateToForInput) {
            return '';
        }
        return `${this.formatDateLocal(this.dateFromForInput)} - ${this.formatDateLocal(this.dateToForInput)}`;
    }

    // неверный период при «Применить»: даты фильтра возвращаются к последнему применённому периоду,
    // а если фильтр на вкладке ещё не применялся — к значениям по умолчанию
    restoreOffBalancePeriod() {
        if (this.offBalanceApplied) {
            this.dateFromForInput = this.isoToDate(this.offBalanceDateFrom);
            this.dateToForInput = this.isoToDate(this.offBalanceDateTo);
        } else {
            this.resetFilterDates();
        }
    }

    // «2026-06-30» → Date в локальном времени 
    isoToDate(value: string): Date {
        const [yyyy, mm, dd] = value.split('-').map(Number);
        return new Date(yyyy, mm - 1, dd);
    }

    // false — фильтр не применяется; крошки и таблица остаются по последнему применённому фильтру
    applyOffBalanceFilter(): boolean {
        if (this.isOffBalancePeriodInvalid()) {
            this.showOffBalancePeriodError();
            this.restoreOffBalancePeriod();
            return false;
        }
        this.offBalanceApplied = true;
        this.offBalanceRegion = this.currRegion?.code || '';
        this.offBalanceRegionText = this.currRegion?.text || '';
        this.offBalanceDateFrom = this.formatDate(this.dateFromForInput);
        this.offBalanceDateTo = this.formatDate(this.dateToForInput);
        this.loadOffBalance();
        return true;
    }

    openBudgetLoader() {
        window.open("https://ea.csi.kz/#/budget-loader", "_blank");
    }

    hasAnyNotLoaded(statusObj: Record<string, string>): boolean {
        return Object.values(statusObj).some(status => status === 'Не загружен');
    }

    activeTab: number = 0; 

    // Забалансовые счета: «Отмена»
    // Несохранённые изменения — отличия от БД (в т. ч. предзаполнение из 31.12) или поле с неверным вводом.
    async onOffBalanceCancel() {
        if (!this.offBalanceEditable) {
            return;
        }
        const hasChanges = this.hasOffBalanceChanges() || Object.keys(this.offBalanceInvalid).length > 0;
        if (hasChanges) {
            const discard = await this.$bvModal.msgBoxConfirm(this.setText('budget_accounting.cancel_confirm'), {
                title: this.setText('budget_accounting.cancel_confirm_title'),
                okTitle: this.setText('budget_accounting.discard_changes'),
                cancelTitle: this.setText('budget_accounting.stay'),
                centered: true,
            });
            if (!discard) {
                return;   // «Остаться» или закрытие окна
            }
        }
        // восстановить сохранённые значения из последнего ответа сервера
        this.buildOffBalanceRows();
    }

}


